import {
  formatPercentPoints,
  type SingleStrainFromWeightOutcome,
  type SingleStrainOutcome,
} from '@shared';

export type SingleStrainResultViewProps =
  | {
      kind: 'target_mg';
      outcome: SingleStrainOutcome;
      brand?: string | null;
      onSaveAsRitual?: () => void;
    }
  | {
      kind: 'target_weight';
      outcome: SingleStrainFromWeightOutcome;
      brand?: string | null;
      onSaveAsRitual?: () => void;
    };

function brandLine(brand: string | null | undefined): string | null {
  const trimmed = brand?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Confirmed single-strain result for Target Weight or Target MG mode.
 */
export function SingleStrainResultView(
  props: SingleStrainResultViewProps,
): JSX.Element {
  if (props.kind === 'target_weight') {
    return <TargetWeightResult outcome={props.outcome} brand={props.brand} onSaveAsRitual={props.onSaveAsRitual} />;
  }
  return <TargetMgResult outcome={props.outcome} brand={props.brand} onSaveAsRitual={props.onSaveAsRitual} />;
}

function TargetWeightResult({
  outcome,
  brand,
  onSaveAsRitual,
}: {
  outcome: SingleStrainFromWeightOutcome;
  brand?: string | null;
  onSaveAsRitual?: () => void;
}): JSX.Element {
  if (!outcome.ok) {
    return (
      <div className="card stack" role="status">
        <h2 className="mt-0">Cannot calculate</h2>
        <div className="notice" role="alert">
          {outcome.message}
        </div>
      </div>
    );
  }

  const decimals =
    outcome.scalePrecisionGrams >= 0.1
      ? 1
      : outcome.scalePrecisionGrams >= 0.01
        ? 2
        : 3;
  const brandLabel = brandLine(brand);

  return (
    <div className="card stack calc-result" aria-live="polite">
      <h2 className="mt-0">Confirmed result</h2>
      <p className="muted" style={{ margin: 0 }}>
        {outcome.strainName}
        {brandLabel ? ` · ${brandLabel}` : ''} ·{' '}
        {formatPercentPoints(outcome.thcPercent)} THC
      </p>
      <div className="calc-result__hero">
        <div>
          <p className="calc-result__label">Joint weight</p>
          <p className="calc-result__value">
            {outcome.weightGrams.toFixed(decimals)} g
          </p>
        </div>
        <div>
          <p className="calc-result__label">Resulting THC</p>
          <p className="calc-result__value">
            {outcome.estimatedThcMg.toFixed(1)} mg
          </p>
        </div>
      </div>
      {!outcome.exactIsMeasurable ? (
        <p className="hint muted">
          That weight is between scale steps ({outcome.scalePrecisionGrams} g).
          Display uses your entered weight for the THC estimate.
        </p>
      ) : null}
      <p className="hint" role="status">
        Calculation confirmed
      </p>
      {onSaveAsRitual ? <button type="button" className="btn btn-primary btn-block" onClick={onSaveAsRitual}>Save Ritual</button> : null}
    </div>
  );
}

function TargetMgResult({
  outcome,
  brand,
  onSaveAsRitual,
}: {
  outcome: SingleStrainOutcome;
  brand?: string | null;
  onSaveAsRitual?: () => void;
}): JSX.Element {
  if (!outcome.ok) {
    return (
      <div className="card stack" role="status">
        <h2 className="mt-0">Cannot calculate</h2>
        <div className="notice" role="alert">
          {outcome.message}
        </div>
      </div>
    );
  }

  const decimals =
    outcome.scalePrecisionGrams >= 0.1
      ? 1
      : outcome.scalePrecisionGrams >= 0.01
        ? 2
        : 3;
  const brandLabel = brandLine(brand);

  return (
    <div className="card stack calc-result" aria-live="polite">
      <h2 className="mt-0">Confirmed result</h2>
      <p className="muted" style={{ margin: 0 }}>
        {outcome.strainName}
        {brandLabel ? ` · ${brandLabel}` : ''} ·{' '}
        {formatPercentPoints(outcome.thcPercent)} THC
      </p>
      <div className="calc-result__hero">
        <div>
          <p className="calc-result__label">Requested THC</p>
          <p className="calc-result__value">{outcome.targetThcMg} mg</p>
        </div>
        <div>
          <p className="calc-result__label">Required weight</p>
          <p className="calc-result__value">
            {outcome.recommendedWeightGrams.toFixed(decimals)} g
          </p>
        </div>
      </div>
      <dl className="detail-grid">
        <div>
          <dt className="muted">Exact weight</dt>
          <dd>{outcome.exactWeightGrams.toFixed(4)} g</dd>
        </div>
        <div>
          <dt className="muted">Estimated THC</dt>
          <dd>{outcome.estimatedThcMg.toFixed(2)} mg</dd>
        </div>
        <div>
          <dt className="muted">Difference from target</dt>
          <dd>
            {outcome.differenceMg >= 0 ? '+' : ''}
            {outcome.differenceMg.toFixed(2)} mg
          </dd>
        </div>
      </dl>
      <p className="hint" role="status">
        Calculation confirmed
      </p>
      {onSaveAsRitual ? <button type="button" className="btn btn-primary btn-block" onClick={onSaveAsRitual}>Save Ritual</button> : null}
    </div>
  );
}
